import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useR2Upload } from "@/hooks/useR2Upload";
import type { MediaType } from "@/interfaces/uploads";
import { fileStemFromUrl } from "@/utils/upload-helper";

interface CropDialogProps {
  imageUrl: string;
  title?: string;
  campaignId: string;
  /** Which R2 media slot the cropped result is uploaded under. */
  mediaType: MediaType;
  /** Receives the R2 public URL of the uploaded crop (not a data URL). */
  onApply: (croppedUrl: string) => void;
  onClose: () => void;
}

export default function CropDialog({
  imageUrl,
  title = "Crop Image",
  campaignId,
  mediaType,
  onApply,
  onClose,
}: CropDialogProps) {
  const { upload, isUploading } = useR2Upload();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const cropStartRef = useRef<{ x: number; y: number } | null>(null);
  const [cropStart, setCropStart] = useState<{ x: number; y: number } | null>(null);
  const [cropEnd, setCropEnd] = useState<{ x: number; y: number } | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [cropError, setCropError] = useState<string | null>(null);

  useEffect(() => {
    const img = new Image();
    // Request the image in CORS mode so the canvas is not tainted and
    // toDataURL() can export the crop. R2 returns Access-Control-Allow-Origin
    // for this origin; without this the export throws a SecurityError.
    // Must be set before `src`.
    img.crossOrigin = "anonymous";
    img.onload = () => {
      // No intrinsic pixel size (e.g. an SVG with only a viewBox) → naturalWidth/Height are 0.
      // Sizing the canvas off that yields a 0×0 canvas and a blank export, so refuse to crop
      // rather than upload an empty image. Leave imgRef unset so Apply/draw are no-ops.
      if (!img.naturalWidth || !img.naturalHeight) {
        setCropError("This image has no fixed dimensions and can't be cropped. Use a PNG or JPG.");
        return;
      }
      imgRef.current = img;
      const canvas = canvasRef.current;
      if (!canvas) return;
      const maxW = 432;
      const scale = Math.min(1, maxW / img.naturalWidth);
      canvas.width = Math.round(img.naturalWidth * scale);
      canvas.height = Math.round(img.naturalHeight * scale);
      const ctx = canvas.getContext("2d");
      ctx?.drawImage(img, 0, 0, canvas.width, canvas.height);
    };
    img.onerror = () => {
      setCropError("Could not load the image for cropping. Please try re-uploading.");
    };
    // Load through a crop-ONLY cache key (`cors=1`) so a copy cached WITHOUT CORS headers by a plain
    // <img> elsewhere (the EmailPreview canvas, landing/redemption previews) can never satisfy this
    // request. A browser reuses a same-URL no-cors cached response for a crossOrigin load, dropping
    // Access-Control-Allow-Origin and tainting the canvas — the CORS error seen in prod. This URL is
    // requested ONLY here and ONLY with crossOrigin, so R2 always answers with the CORS header. The
    // param is ignored by R2 (served by key); `imageUrl` (without it) still names the crop in
    // handleApply, so re-crops keep a stable key.
    const sep = imageUrl.includes("?") ? "&" : "?";
    img.src = `${imageUrl}${sep}cors=1`;
  }, [imageUrl]);

  function getCanvasPos(e: React.MouseEvent<HTMLCanvasElement>) {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(canvas.width, e.clientX - rect.left)),
      y: Math.max(0, Math.min(canvas.height, e.clientY - rect.top)),
    };
  }

  function redraw(start: { x: number; y: number } | null, end: { x: number; y: number } | null) {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    if (start && end) {
      const x = Math.min(start.x, end.x);
      const y = Math.min(start.y, end.y);
      const w = Math.abs(end.x - start.x);
      const h = Math.abs(end.y - start.y);
      ctx.fillStyle = "rgba(0,0,0,0.45)";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.clearRect(x, y, w, h);
      ctx.drawImage(
        img,
        (x / canvas.width) * img.naturalWidth,
        (y / canvas.height) * img.naturalHeight,
        (w / canvas.width) * img.naturalWidth,
        (h / canvas.height) * img.naturalHeight,
        x,
        y,
        w,
        h,
      );
      ctx.strokeStyle = "#fff";
      ctx.lineWidth = 1.5;
      ctx.strokeRect(x + 0.5, y + 0.5, w - 1, h - 1);
    }
  }

  function handleMouseDown(e: React.MouseEvent<HTMLCanvasElement>) {
    const pos = getCanvasPos(e);
    cropStartRef.current = pos;
    setCropStart(pos);
    setCropEnd(pos);
    setIsDrawing(true);
    setCropError(null);
  }

  function handleMouseMove(e: React.MouseEvent<HTMLCanvasElement>) {
    if (!isDrawing) return;
    const pos = getCanvasPos(e);
    setCropEnd(pos);
    redraw(cropStartRef.current, pos);
  }

  function handleMouseUp(e: React.MouseEvent<HTMLCanvasElement>) {
    if (!isDrawing) return;
    const pos = getCanvasPos(e);
    setCropEnd(pos);
    setIsDrawing(false);
    redraw(cropStartRef.current, pos);
  }

  function handleReset() {
    setCropStart(null);
    setCropEnd(null);
    cropStartRef.current = null;
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img) return;
    const ctx = canvas.getContext("2d");
    ctx?.clearRect(0, 0, canvas.width, canvas.height);
    ctx?.drawImage(img, 0, 0, canvas.width, canvas.height);
  }

  async function handleApply() {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img || !cropStart || !cropEnd) {
      onClose();
      return;
    }
    const x = Math.min(cropStart.x, cropEnd.x);
    const y = Math.min(cropStart.y, cropEnd.y);
    const w = Math.abs(cropEnd.x - cropStart.x);
    const h = Math.abs(cropEnd.y - cropStart.y);
    if (w < 5 || h < 5) {
      setCropError("Selection is too small. Please drag a larger area.");
      return;
    }
    const scaleX = img.naturalWidth / canvas.width;
    const scaleY = img.naturalHeight / canvas.height;
    const out = document.createElement("canvas");
    out.width = Math.round(w * scaleX);
    out.height = Math.round(h * scaleY);
    const outCtx = out.getContext("2d");
    if (!outCtx) {
      onClose();
      return;
    }
    outCtx.drawImage(
      img,
      x * scaleX,
      y * scaleY,
      w * scaleX,
      h * scaleY,
      0,
      0,
      out.width,
      out.height,
    );

    // Serialize the crop to a real PNG and upload it to R2, then hand the parent
    // the public URL. The crop is a distinct asset (email/landing can't crop at
    // render time), so it must live in R2 like any other uploaded media — never
    // persisted as a base64 data URL in the campaign row.
    let blob: Blob | null;
    try {
      blob = await new Promise<Blob | null>((resolve) => out.toBlob(resolve, "image/png"));
    } catch {
      setCropError("Could not export the crop. The image could not be read securely.");
      return;
    }
    if (!blob) {
      setCropError("Could not export the crop. Please try again.");
      return;
    }

    // Name the crop after the SOURCE image (the original), so the key becomes
    // `{originalName}-cropped.png` — the server strips any -original/-cropped marker and re-appends
    // the variant. Derived from the stable original URL, so re-crops keep the same key → overwrite.
    const stem = fileStemFromUrl(imageUrl) || mediaType;
    const file = new File([blob], `${stem}.png`, { type: "image/png" });
    try {
      // "cropped" variant → deterministic key, so re-cropping overwrites the same object
      // (one cropped file per slot) instead of accumulating a new upload each time.
      const publicUrl = await upload(file, mediaType, campaignId, "cropped");
      onApply(publicUrl);
    } catch {
      setCropError("Could not save the cropped image. Please try again.");
    }
  }

  return createPortal(
    // biome-ignore lint/a11y/noStaticElementInteractions: modal backdrop dismiss on click-outside
    <div
      role="presentation"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.6)",
        zIndex: 9000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          background: "white",
          borderRadius: 12,
          padding: 24,
          width: 480,
          maxWidth: "90vw",
          boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
        }}
      >
        <div
          style={{
            fontFamily: "var(--font-crimson)",
            fontWeight: 400,
            fontSize: 20,
            color: "var(--color-rodeo-black)",
            marginBottom: 16,
          }}
        >
          {title}
        </div>
        <div
          style={{
            background: "#f0f0f0",
            borderRadius: 8,
            overflow: "hidden",
            marginBottom: 16,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 200,
          }}
        >
          <canvas
            ref={canvasRef}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            style={{ display: "block", cursor: "crosshair", maxWidth: "100%" }}
          />
        </div>
        <p
          style={{
            fontFamily: "var(--font-lato)",
            fontSize: 12,
            color: "var(--color-graphite)",
            marginBottom: 16,
          }}
        >
          Click and drag to select the area you want to keep.
        </p>
        {cropError && (
          <p
            style={{
              fontFamily: "var(--font-lato)",
              fontSize: 12,
              color: "var(--color-wine)",
              marginBottom: 16,
              marginTop: -8,
            }}
          >
            {cropError}
          </p>
        )}
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button
            type="button"
            onClick={handleReset}
            disabled={isUploading}
            style={{
              padding: "8px 20px",
              fontFamily: "var(--font-lato)",
              fontSize: 13,
              fontWeight: 600,
              borderRadius: 6,
              cursor: isUploading ? "not-allowed" : "pointer",
              border: "1px solid var(--color-smoke)",
              background: "white",
              color: "var(--color-graphite)",
              marginRight: "auto",
              opacity: isUploading ? 0.6 : 1,
              transition: "all 0.15s",
            }}
          >
            Reset
          </button>
          <button
            type="button"
            onClick={onClose}
            disabled={isUploading}
            style={{
              padding: "8px 20px",
              fontFamily: "var(--font-lato)",
              fontSize: 13,
              fontWeight: 600,
              borderRadius: 6,
              cursor: isUploading ? "not-allowed" : "pointer",
              border: "1px solid var(--color-smoke)",
              background: "white",
              color: "var(--color-graphite)",
              opacity: isUploading ? 0.6 : 1,
              transition: "all 0.15s",
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleApply}
            disabled={isUploading}
            style={{
              padding: "8px 20px",
              fontFamily: "var(--font-lato)",
              fontSize: 13,
              fontWeight: 600,
              borderRadius: 6,
              cursor: isUploading ? "not-allowed" : "pointer",
              border: "none",
              background: "var(--color-dark-dust)",
              color: "white",
              opacity: isUploading ? 0.7 : 1,
              transition: "all 0.15s",
            }}
          >
            {isUploading ? "Applying…" : "Apply Crop"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
